import React, { ReactElement, useEffect, useState, useReducer, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import InvoiceForm from '../../components/InvoiceForm'
import Radom, { Customer, IProduct, MultiCreateInvoice, Invoice } from '../../state/Radom'
import Web3 from '../../state/User'
import { PAY_BASE_URL } from '../../util/Constants'
import { errorToast, successToast } from '../../util/Util'
import { AxiosError } from 'axios'
import Preview from '../../components/Preview'

const qaTimestamp = '2026-04-21T00:00:00.000Z'

const qaCustomer: Customer = {
  id: 'qa-customer-northstar',
  totalSpend: '18420',
  email: 'finance@northstar.example',
  name: 'Northstar Supply Co.',
  billingAddress: '120 Moorgate, London EC2M 6UR, United Kingdom',
  residenceCountry: 'GB',
  phone: '+44 20 7946 0958',
  telegram: '',
  discord: '',
  payments: [],
  tags: ['QA', 'Priority'],
  createdAt: qaTimestamp,
  accountType: 'Business',
  companyName: 'Northstar Supply Co.',
  companyNumber: '12345678'
}

const qaInvoiceProduct: IProduct = {
  id: 'qa-product-platform-access',
  name: 'Platform access',
  description: 'Annual platform access for payment operations.',
  addOns: [],
  chargingIntervalSeconds: 0,
  currency: 'GBP',
  price: 1200,
  quantity: 1,
  sendSubscriptionEmails: false,
  createdAt: qaTimestamp,
  updatedAt: qaTimestamp,
  imageUrl: '',
  isArchived: false
}

export enum FormStep {
  INVOICE,
  CONFIRM,
}

export const defaultCustomer: Customer = {
  id: '',
  name: 'Example Customer',
  email: ''
}
export const defaultInvoice: MultiCreateInvoice = {
  gateway: {
    managed: {
      methods: []
    }
  },
  customer: defaultCustomer,
  products: [],
  lineItems: [],
  customers: [],
  overdueAt: undefined,
  memo: undefined,
  inputData: [],
  url: ''
}

const CreateInvoice = (): ReactElement => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isQaSeed = searchParams.get('qa') === '1'

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [formStep, setFormStep] = useState(FormStep.INVOICE)

  const [previewWidth, setPreviewWidth] = useState('90%')

  const [invoiceForm, updateInvoiceForm] = useReducer(
    (prev: MultiCreateInvoice, next: Partial<MultiCreateInvoice>) => { return { ...prev, ...next } }, defaultInvoice)

  const sellerAddress = useMemo(() => {
    if (Web3.isConnected) {
      return Web3.accounts[0]
    }
  }, [Web3.isConnected])

  const previewData = useMemo(() => {
    return Buffer.from(JSON.stringify({
      ...invoiceForm,
      seller: Radom.sellerProfile ?? (isQaSeed ? { name: 'Radom QA Studio' } : undefined),
      sellerAddress,
      customer: invoiceForm.customers[0] ?? defaultCustomer
    }), 'utf8').toString('base64')
  }, [invoiceForm, isQaSeed])

  const onInvoiceCreate = (): void => {
    setIsSubmitting(true)

    Radom.createInvoice(invoiceForm)
      .then((invoices) => {
        successToast('Invoice created')
        const id = invoices[0].id
        navigate(`/invoices/${id}`)
      })
      .catch((err: AxiosError) => {
        const responseData = err?.response?.data
        const msg = typeof responseData === 'string' ? responseData : err.code ?? 'Unknown error'
        errorToast(`Error: ${msg}`)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onFormChange = (form: Partial<Invoice>): void => {
    updateInvoiceForm(form)
  }

  useEffect(() => {
    if (!isQaSeed) {
      return
    }

    const overdueAt = new Date()
    overdueAt.setDate(overdueAt.getDate() + 14)

    updateInvoiceForm({
      gateway: {
        managed: {
          methods: [
            { network: 'Ethereum' },
            { network: 'Base' },
            { network: 'Ethereum', token: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' },
            { network: 'Base', token: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' },
            { network: 'Solana', token: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' }
          ]
        }
      },
      customers: [qaCustomer],
      customer: qaCustomer,
      products: [{ product: qaInvoiceProduct, quantity: 1 }],
      lineItems: [
        {
          invoiceId: '',
          name: 'Implementation workshop',
          quantity: 1,
          price: 450,
          currency: 'GBP'
        },
        {
          invoiceId: '',
          name: 'Priority support credit',
          quantity: 2,
          price: 125,
          currency: 'GBP'
        }
      ],
      overdueAt,
      memo: 'Thanks for choosing Radom. This QA invoice includes a saved customer, product, line items, and several accepted payment methods.',
      inputData: [],
      url: `${PAY_BASE_URL}/invoice/qa-invoice-designer`
    })
  }, [isQaSeed])

  return <div style={{ display: 'flex', height: '100vh', position: 'relative' }}>
    <InvoiceForm
      invoice={invoiceForm}
      isSubmitting={isSubmitting}
      isLoading={false}
      formStep={formStep}
      skipRemoteData={isQaSeed}
      onFormStep={step => setFormStep(step)}
      onFormChange={onFormChange}
      onFormSubmit={onInvoiceCreate}
    />
    <Preview
      iframeLink={`${PAY_BASE_URL}/preview/invoice#${previewData}`}
      previewWidth={previewWidth}
      onWidthChange={setPreviewWidth}
      iframeTitle='pay.radom.com/invoice'
    />
  </div>
}

export default CreateInvoice
